// @flow

//import styles
import styles from "./ConfirmOptions.scss";

//import libs
import React, { useState, useRef, useEffect } from "react";

//import Components
import Options from "./Options/Options";
import SelectOptionList from "./Options/SelectList";
import SelectList from "../DefineEvents/SelectList/SelectList";
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormHelperText,
    Grid,
    TextField,
    Typography,
} from "@material-ui/core";
import get from "lodash.get";

type Props = {
    translate: *,
    /** Trigger modal close */
    onClose: () => void,
    /** Trigger */
    onSubmit: (option: *) => void,
    options?: *,
    loading: boolean,
    confirmOptionsState: *,
    categories?: *,
    initialCategoryUuid?: ?string,
    isCategoryRequired?: boolean,
};

/**
 * confirm options dialog
 */
const ConfirmOptions = ({
    translate,
    onClose,
    onSubmit,
    options: optionsProp,
    categories,
    loading,
    confirmOptionsState,
    initialCategoryUuid,
    isCategoryRequired,
}: Props) => {
    const [globalStatus, setGlobalStatus] = useState("");
    const [showPast, setShowPast] = useState(false);
    const [category, setCategory] = useState("");
    const optionsRef = useRef(null);

    useEffect(() => {
        setCategory(initialCategoryUuid || "");
    }, [initialCategoryUuid]);

    const eventName = optionsProp && optionsProp.seriesName;
    const optionCode = optionsProp && optionsProp.seriesOptionCode;
    const options = optionsProp && optionsProp.options;

    const disabled = confirmOptionsState.loading;

    /**
     * Get errors
     */
    const getError = (key: string) => {
        let transformedKey = key;

        if (confirmOptionsState.fieldErrors) {
            return get(confirmOptionsState.fieldErrors, transformedKey);
        }
    };

    const onChangeAll = (value: string) => {
        optionsRef.current && optionsRef.current.onChangeAll(value);
        setGlobalStatus(value);
    };
    return (
        <Dialog open maxWidth="md" className={styles.element}>
            <DialogTitle disableTypography className={styles.title}>
                <Grid container spacing={3} alignItems="center">
                    <Grid item>
                        <Typography variant="h2" color="inherit">
                            {translate("Confirm options")}
                        </Typography>
                    </Grid>
                    <Grid item xs={6}>
                        <Typography color="inherit" style={{ opacity: "0.5" }}>
                            {translate(
                                "Choose which options you would like to confirm for the series:",
                            )}
                        </Typography>
                        <FormControlLabel
                            value="showPast"
                            label={translate("Show past options")}
                            color="inherit"
                            style={{ opacity: "0.5" }}
                            control={
                                <Checkbox
                                    onChange={e =>
                                        setShowPast(e.target.checked)
                                    }
                                    style={{ color: "#fff" }}
                                />
                            }
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            className={styles.disabledInput}
                            disabled
                            InputLabelProps={{
                                shrink: true,
                            }}
                            id="confirmoptions-seriesname"
                            label="Event series"
                            value={`${eventName || ""}${
                                eventName && optionCode ? " - " : ""
                            }${optionCode || ""}`}
                            margin="normal"
                            fullWidth
                        />
                    </Grid>
                    <Grid
                        color="inherit"
                        style={{
                            opacity: "0.5",
                            display: "flex",
                            alignItems: "center",
                        }}
                        item
                        xs={6}
                        offset={6}
                    >
                        <SelectList
                            id="confirm-options-chooseCategory"
                            onSelect={value => setCategory(value)}
                            venueCode="SEA-BLUE"
                            optionStatus={category}
                            color="light"
                            categories={categories}
                            type="CATEGORY"
                            required={isCategoryRequired}
                            error={!!getError("categoryUuid")}
                            helperText={getError("categoryUuid")}
                            label={translate("Category")}
                        />
                    </Grid>
                    <Grid
                        color="inherit"
                        style={{
                            opacity: "0.5",
                            display: "flex",
                            alignItems: "center",
                        }}
                        item
                        xs={6}
                        offset={6}
                    >
                        <Typography
                            color="inherit"
                            style={{ flexShrink: 0, marginRight: "1em" }}
                        >
                            {translate("Change all to: ")}
                        </Typography>
                        <SelectOptionList
                            id="confirmoptions-setStatusToAll"
                            onSelect={value => onChangeAll(value)}
                            venueCode="SEA-BLUE"
                            optionStatus={globalStatus}
                            disabled={disabled}
                            color="light"
                        />
                    </Grid>
                </Grid>
            </DialogTitle>
            <DialogContent dividers>
                {loading && (
                    <Grid container style={{ alignItems: "flex-center" }}>
                        <CircularProgress className={styles.spinner} />
                    </Grid>
                )}

                {!loading && options && (
                    <Options
                        options={options}
                        translate={translate}
                        seriesName={eventName}
                        optionCode={optionCode}
                        onSubmit={opts => onSubmit(opts, category)}
                        ref={optionsRef}
                        confirmOptionsState={confirmOptionsState}
                        disabled={disabled}
                        showPast={showPast}
                    />
                )}
            </DialogContent>

            <DialogActions>
                {confirmOptionsState && confirmOptionsState.error && (
                    <FormHelperText error>
                        {confirmOptionsState.error}
                    </FormHelperText>
                )}
                <Grid item xs="auto">
                    <Button id="editoption-cancel" onClick={onClose}>
                        {translate("Cancel")}
                    </Button>
                </Grid>

                <Grid item xs="auto">
                    <Button
                        id="editoption-submit"
                        form="confirmOptions"
                        type="submit"
                        color="primary"
                        disabled={disabled}
                    >
                        {translate("Confirm")}
                    </Button>
                </Grid>
            </DialogActions>
        </Dialog>
    );
};

export default ConfirmOptions;
